// Browse view - displays all laws with pagination and search
// Refactored to use shared law card renderer

import templateHtml from '@views/templates/browse.html?raw';
import { fetchLaws } from '../utils/api.ts';
import { renderLoadingHTML } from '../components/loading.ts';
import { getRandomLoadingMessage, LAWS_PER_PAGE } from '../utils/constants.ts';
import { addVotingListeners } from '../utils/voting.ts';
import { hydrateIcons } from '@utils/icons.ts';
import { renderLawCards } from '../utils/law-card-renderer.ts';
import { initSharePopovers } from '../components/social-share.ts';
import { renderPagination } from '../utils/pagination.ts';
import { updateSearchInfo, hasActiveFilters } from '../utils/search-info.ts';
import { AdvancedSearch } from '../components/advanced-search.ts';
import { TopVoted } from '../components/top-voted.ts';
import { Trending } from '../components/trending.ts';
import { RecentlyAdded } from '../components/recently-added.ts';
import { triggerAdSense } from '../utils/ads.ts';
import { setExportContent, clearExportContent, ContentType } from '../utils/export-context.ts';
import { Breadcrumb } from '../components/breadcrumb.ts';
import { handleCopyAction } from '../utils/copy-actions.ts';
import { handleNavClick, addNavigationListener } from '../utils/navigation.ts';
import { updatePageMetadata } from '../utils/dom.ts';
import { trackProductEvent } from '@utils/metrics.ts';
import type { CleanableElement, OnNavigate, SearchFilters, Law } from '../types/app.ts';

const BROWSE_TITLE = "Browse All Murphy's Laws | Murphy's Law Archive";
const BROWSE_DESCRIPTION =
  "Search and filter the complete collection of Murphy's Laws. Find corollaries, technology laws, and everyday observations about the perversity of the universe.";

function parseBrowseParams(search: string): { filters: SearchFilters; sort: string; order: string; page: number } {
  const params = new URLSearchParams(search);
  const q = params.get('q') ?? '';
  const categoryId = params.get('category_id');
  const attribution = params.get('attribution') ?? '';
  const sort = params.get('sort') ?? (q ? 'relevance' : 'score');
  const order = params.get('order') ?? 'desc';
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
  const filters: SearchFilters = { q };
  if (categoryId) filters.category_id = /^\d+$/.test(categoryId) ? parseInt(categoryId, 10) : categoryId;
  if (attribution) filters.attribution = attribution;
  return { filters, sort, order, page };
}

function buildBrowseSearch(filters: SearchFilters, sort: string, order: string, page: number): string {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.category_id !== undefined && filters.category_id !== '') params.set('category_id', String(filters.category_id));
  if (filters.attribution) params.set('attribution', filters.attribution);
  const defaultSort = filters.q ? 'relevance' : 'score';
  if (sort && sort !== defaultSort) params.set('sort', sort);
  if (order && order !== 'desc') params.set('order', order);
  if (page > 1) params.set('page', String(page));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function Browse({ searchQuery, onNavigate }: { searchQuery?: string; onNavigate: OnNavigate }): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'container page';

  const initial = parseBrowseParams(typeof location !== 'undefined' ? location.search : '');
  let currentPage = initial.page;
  let totalLaws = 0;
  let laws: Law[] = [];
  let currentFilters: SearchFilters = searchQuery !== undefined && searchQuery !== '' ? { ...initial.filters, q: searchQuery } : initial.filters;
  let currentSort = searchQuery ? 'relevance' : initial.sort;
  let currentOrder = initial.order;

  // Render law cards
  function renderLaws(laws: Law[], query?: string) {
    if (!laws || laws.length === 0) {
      return `
        <div class="empty-state">
          <span class="icon empty-state-icon" data-icon="searchOff" aria-hidden="true"></span>
          <p class="empty-state-title">Murphy spared these results</p>
          <p class="empty-state-text">Try adjusting your search filters or clearing them to see more results.</p>
          <button class="btn mt-4" data-nav="submit">
            <span class="btn-text">Submit a Law</span>
            <span class="icon" data-icon="send" aria-hidden="true"></span>
          </button>
        </div>
      `;
    }

    // Use shared law card renderer with search highlighting (eliminates ~30 lines of duplicate HTML)
    return renderLawCards(laws, { searchQuery: query });
  }

  // Render the page
  async function render() {
    updatePageMetadata({ title: BROWSE_TITLE, description: BROWSE_DESCRIPTION, path: '/browse' });
    el.innerHTML = templateHtml;
    await updateSearchInfo(el.querySelector('#browse-search-info'), currentFilters);

    const breadcrumbContainer = el.querySelector('#browse-breadcrumb')!;
    const breadcrumb = Breadcrumb({
      items: [{ label: 'Browse All Murphy\'s Laws' }],
      onNavigate
    });
    if (breadcrumb) breadcrumbContainer.replaceChildren(breadcrumb);

    const loadingPlaceholder = el.querySelector('.loading-placeholder p');
    if (loadingPlaceholder) loadingPlaceholder.textContent = getRandomLoadingMessage();
  }

  async function updateDisplay() {
    const cardText = el.querySelector('#browse-laws-list')!;
    cardText.setAttribute('aria-busy', 'false');
    cardText.innerHTML = `
      ${renderLaws(laws, currentFilters.q)}
      ${renderPagination(currentPage, totalLaws, LAWS_PER_PAGE)}
    `;
    hydrateIcons(cardText);
    initSharePopovers(cardText as HTMLElement);

    const resultCountEl = el.querySelector('#browse-result-count') as HTMLElement;
    if (totalLaws > 0) {
      const start = (currentPage - 1) * LAWS_PER_PAGE + 1;
      const end = Math.min(currentPage * LAWS_PER_PAGE, totalLaws);
      resultCountEl.textContent = `Showing ${start}-${end} of ${totalLaws} laws`;
      resultCountEl.hidden = false;
    } else {
      resultCountEl.textContent = '';
      resultCountEl.hidden = true;
    }

    await updateSearchInfo(el.querySelector('#browse-search-info'), currentFilters);
  }

  // Load laws for current page
  async function loadPage(page: number) {
    currentPage = page;

    const cardText = el.querySelector('#browse-laws-list')!;
    cardText.setAttribute('aria-busy', 'true');
    /* Disable pagination buttons before replacing content so they are still in DOM */
    el.querySelectorAll('.pagination button').forEach(btn => {
      btn.setAttribute('disabled', 'true');
    });
    cardText.innerHTML = renderLoadingHTML();

    try {
      const offset = (page - 1) * LAWS_PER_PAGE;
      const data = await fetchLaws({
        limit: LAWS_PER_PAGE,
        offset,
        sort: currentSort,
        order: currentOrder,
        ...currentFilters
      });

      laws = data && Array.isArray(data.data) ? data.data : [];
      totalLaws = data && Number.isFinite(data.total) ? data.total : laws.length;
      if (laws.length === 0 && hasActiveFilters(currentFilters)) {
        trackProductEvent('archive.no_results', { surface: 'browse' });
      }
      await updateDisplay();

      // Register export content
      if (laws.length > 0) {
        setExportContent({
          type: ContentType.LAWS,
          title: hasActiveFilters(currentFilters) ? 'Search Results' : 'All Laws',
          data: laws,
          metadata: { total: totalLaws, filters: currentFilters, page: currentPage }
        });
      } else {
        clearExportContent();
      }

      if (laws.length > 0) triggerAdSense(cardText as HTMLElement);

      const search = buildBrowseSearch(currentFilters, currentSort, currentOrder, currentPage);
      if (typeof history !== 'undefined' && history.replaceState) {
        history.replaceState(history.state ?? {}, '', `${location.pathname}${search}`);
      }
    } catch {
      cardText.setAttribute('aria-busy', 'false');
      cardText.innerHTML = `
        <div class="empty-state">
          <span class="icon empty-state-icon" data-icon="error" aria-hidden="true"></span>
          <p class="empty-state-title">Of course something went wrong</p>
          <p class="empty-state-text">Ironically, Murphy's Laws couldn't be loaded right now. Please try again.</p>
          <button type="button" class="btn" data-action="retry">
            <span class="icon" data-icon="refresh" aria-hidden="true"></span>
            <span class="btn-text">Try Again</span>
          </button>
        </div>
      `;
      try {
        hydrateIcons(cardText);
      } catch (iconErr) {
        console.error('Failed to hydrate error state icons:', iconErr);
      }
    }
  }

  // Event delegation for navigation, copy actions, pagination, and law card clicks
  // Note: Voting is handled by addVotingListeners() utility
  el.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-action="retry"]')) {
      loadPage(currentPage);
      return;
    }

    // Handle copy text/link actions (shared utility) - sync guard, async clipboard
    if (t.closest('[data-action="copy-text"]') || t.closest('[data-action="copy-link"]')) {
      void handleCopyAction(e, t);
      return;
    }

    // Handle navigation buttons and links (shared utility)
    if (handleNavClick(t, onNavigate)) {
      e.preventDefault();
      return;
    }

    // Handle page navigation (use closest so clicks on button inner span/icon still work)
    const pageBtn = t.closest('button[data-page]');
    if (pageBtn && !pageBtn.hasAttribute('disabled')) {
      const pageAttr = pageBtn.getAttribute('data-page');
      if (pageAttr) {
        const page = parseInt(pageAttr, 10);
        if (!isNaN(page) && page > 0) {
          trackProductEvent('archive.pagination', { surface: 'browse', action: page > currentPage ? 'next' : 'previous' });
          loadPage(page);
        }
      }
      return;
    }

    // Handle law card clicks
    const lawCard = t.closest('.law-card-mini') as HTMLElement | null;
    if (lawCard && lawCard.dataset.lawId) {
      // Don't navigate if clicking on interactive elements (buttons for voting, favorites, share)
      if (t.closest('button')) return;
      trackProductEvent('archive.result_open', { surface: 'browse' });
      onNavigate('law', lawCard.dataset.lawId);
      return;
    }
  });

  // Keyboard navigation for law cards (WCAG 2.1.1) - shared utility
  addNavigationListener(el, onNavigate);

  // Initial render and load (catch so rejections don't trigger global error banner)
  void render().catch((err) => {
    console.error('Browse initial render failed:', err);
  });

  // Add voting listeners using shared utility (replaces 35 lines of duplicate code)
  addVotingListeners(el);

  // Function to update widgets visibility based on search state
  function updateWidgetsVisibility() {
    const widgetsContainer = el.querySelector('[data-widgets]');
    if (widgetsContainer) {
      if (hasActiveFilters(currentFilters)) {
        widgetsContainer.setAttribute('hidden', '');
      } else {
        widgetsContainer.removeAttribute('hidden');
      }
    }
  }

  const searchComponent = AdvancedSearch({
    initialFilters: currentFilters,
    onSearch: (filters) => {
      trackProductEvent('archive.search', { surface: 'browse', result: 'submitted' });
      currentFilters = filters;
      currentSort = filters.q ? 'relevance' : 'score';
      currentOrder = 'desc';
      updateWidgetsVisibility();
      currentPage = 1;
      loadPage(1);
    }
  });
  el.querySelector('#advanced-search-container')!.appendChild(searchComponent);

  const widgetsContainer = el.querySelector('[data-widgets]')!;
  const seenWidgetLawIds = new Set<number>();
  widgetsContainer.appendChild(TopVoted({ seenIds: seenWidgetLawIds }));
  widgetsContainer.appendChild(Trending({ seenIds: seenWidgetLawIds }));
  widgetsContainer.appendChild(RecentlyAdded({ seenIds: seenWidgetLawIds }));

  updateWidgetsVisibility();

  const sortSelect = el.querySelector('#sort-select') as HTMLSelectElement;
  const sortValue = `${currentSort}-${currentOrder}`;
  if (sortSelect && sortValue) {
    const option = sortSelect.querySelector(`option[value="${sortValue}"]`);
    if (option) (option as HTMLOptionElement).selected = true;
  }
  sortSelect?.addEventListener('change', (e) => {
    const value = (e.target as HTMLSelectElement).value;
    const [sort, order] = value.split('-');
    currentSort = sort ?? '';
    currentOrder = order ?? '';
    currentPage = 1;
    loadPage(1);
  });

  void loadPage(currentPage).catch((err) => {
    console.error('Browse loadPage failed:', err);
  });

  // Cleanup function to clear export content on unmount
  (el as CleanableElement).cleanup = () => {
    clearExportContent();
  };

  return el;
}
